import { type FC, useRef } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  ActionButton,
  ActionButtonIconPlacement,
  ActionButtonIntent,
  Icons,
  Item,
  MainMenuOptionItem,
  MenuListBox,
  Popover,
  Section,
  useContextualMenu,
} from "widgets";

import { BrowserSupportLevel, browserSupportLevel } from "@/app-utils/browser-compatibility.tsx";
import { useRouteTo } from "@/app-utils/hooks/useRouteTo.ts";
import type { Configuration } from "@/graphql.ts";
import { isLicensed } from "@/redux/modules/config.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { GETTING_STARTED_ELEMENT_ID, LICENSE_MODULE } from "@/settings/settings.ts";

export const ContentMenu: FC = () => {
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration, shallowEqual);
  const routeTo = useRouteTo();
  const isUnsupportedBrowser = browserSupportLevel === BrowserSupportLevel.UNSUPPORTED;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const { state, triggerProps, menuProps } = useContextualMenu(triggerRef);

  return (
    <>
      <ActionButton
        {...triggerProps}
        id={GETTING_STARTED_ELEMENT_ID.CONTENT_BROWSER}
        ref={triggerRef}
        label={"Content"}
        intent={state.isOpen ? ActionButtonIntent.Active : ActionButtonIntent.Secondary}
        icon={"keyboard_arrow_down"}
        iconPlacement={ActionButtonIconPlacement.Right}
      />
      <Popover state={state} triggerRef={triggerRef} placement={"bottom start"}>
        <MenuListBox
          {...menuProps}
          onAction={(key) => {
            switch (key) {
              case "nav-bar-dashboard":
                routeTo(isUnsupportedBrowser ? "/unsupported" : "/home");
                break;
              case "nav-bar-content-genomes":
                routeTo("/content/genomes");
                break;
              case "nav-bar-content-upload-vectors":
                routeTo("/content/vectors");
                break;
              case "nav-bar-content-saved-items":
                routeTo("/content/savedItems");
                break;
              case "navbar-menu-content-playbook-events":
                routeTo("/content/events");
                break;
              case "navbar-menu-content-files":
                routeTo("/content/files");
                break;
              case "navbar-menu-content-analyst-sessions":
                routeTo("/content/ssaSessions");
                break;
            }
          }}
        >
          <Section>
            <Item key={"nav-bar-dashboard"}>
              <MainMenuOptionItem
                title={"Dashboard"}
                id={"nav-bar-dashboard"}
                icon={<img src={Icons.Tiles.Dashboard} width={30} height={30} alt="" />}
                description={"Home"}
              />
            </Item>
          </Section>
          <Section>
            <Item key={"nav-bar-content-genomes"}>
              <MainMenuOptionItem
                title={"Genomes"}
                id={"nav-bar-content-genomes"}
                icon={<img src={Icons.Tiles.Entity} width={30} height={30} alt="" />}
                description={"Organisms and sequences with annotations and metadata"}
              />
            </Item>
            {isLicensed(configuration, LICENSE_MODULE.EXPLORER) && (
              <Item key={"nav-bar-content-upload-vectors"}>
                <MainMenuOptionItem
                  id={"nav-bar-content-upload-vectors"}
                  icon={<img src={Icons.Tiles.Sequences} width={30} height={30} alt="" />}
                  title={"Vectors"}
                  description={"Vector sequences and annotations"}
                />
              </Item>
            )}
          </Section>
          <Section>
            <Item key={"nav-bar-content-saved-items"}>
              <MainMenuOptionItem
                id={"nav-bar-content-saved-items"}
                icon={<img src={Icons.Tiles.SavedItems} width={30} height={30} alt="" />}
                title={"Saved Items"}
                description={"Views and groups that have been saved to projects"}
              />
            </Item>
            <Item key={"navbar-menu-content-playbook-events"}>
              <MainMenuOptionItem
                id={"navbar-menu-content-playbook-events"}
                icon={<img src={Icons.Tiles.Events} width={30} height={30} alt="" />}
                title={"Events"}
                description={"Recent Playbook activities associated with your projects"}
              />
            </Item>
            <Item key={"navbar-menu-content-files"}>
              <MainMenuOptionItem
                id={"navbar-menu-content-files"}
                icon={<img src={Icons.Tiles.FileBrowser} width={30} height={30} alt="" />}
                title={"Files"}
                description={"Raw data, documents, and related content"}
              />
            </Item>
          </Section>
          {isLicensed(configuration, LICENSE_MODULE.ANALYST) && (
            <Section>
              <Item key={"navbar-menu-content-analyst-sessions"}>
                <MainMenuOptionItem
                  id={"navbar-menu-content-analyst-sessions"}
                  icon={<img src={Icons.Tiles.Analyst} width={30} height={30} alt="" />}
                  title={"Analyst Sessions"}
                  description={"Processed data with annotations and analysis results"}
                />
              </Item>
            </Section>
          )}
        </MenuListBox>
      </Popover>
    </>
  );
};
